// 設定 › 通知 › 節點與群組（SPEC #org-tree-ui 第 5 點、#org-tree-impact「事件與告警路由」、o25）：
//   - 頁首：組織樹上的節點預設不推一般偵測；訂閱後才推播 / 進收件匣；沒訂閱的只在「事件」清單看得到
//   - 我訂閱的：節點（委派的從「委派：<委派方>」起）/ 群組，一般偵測的推播 / 收件匣 / Email，〔取消訂閱〕；路徑失效時保留但標「目前不生效」
//   - 〔＋ 訂閱節點或群組〕：只列我有路徑的節點與我看得到的群組
//   - 不用訂閱就會收到（唯讀）：警報（能處理警報的地方照推；觀看者不推）、設備異常（節點管理員 / 維運）；委派方要求兩步驟驗證時的警報
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { meTreeApi, SUB_CATEGORIES, type MeTreeItem, type ScopeSubscription } from '../../lib/orgTreeApi';
import { levelText } from '../../lib/levels';
import { toggle } from './DelegatedNodePicker';
import { useViewsAndGroups } from './hooks';
import { otErr } from './otErr';

type Ch = 'push' | 'inbox' | 'email';

export function NodeSubscriptions() {
  const { t } = useTranslation();
  const { planes, orgs } = useScope();
  const qc = useQueryClient();
  const p = planes[0];
  const subs = useQuery({ queryKey: ['me-tree', 'subs', p?.api], queryFn: () => meTreeApi.subscriptions(p), enabled: !!p, retry: false });
  const { groups } = useViewsAndGroups(planes, orgs.filter((o) => o.kind === 'team'));
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!p) return null;
  const refresh = () => void qc.invalidateQueries({ queryKey: ['me-tree', 'subs'] });
  const target = (s: ScopeSubscription) => (s.group_id ? { group_id: s.group_id } : { node_id: s.node_id!, delegation_id: s.delegation_id || undefined });
  const on = (s: ScopeSubscription, ch: Ch) => SUB_CATEGORIES.some((c) => s.categories?.[c]?.[ch]);
  async function setCh(s: ScopeSubscription, ch: Ch, v: boolean) {
    setErr(null);
    const cats = Object.fromEntries(SUB_CATEGORIES.map((c) => [c, { push: on(s, 'push'), inbox: on(s, 'inbox'), email: on(s, 'email'), [ch]: v }]));
    try {
      await meTreeApi.subscribe(p, target(s), cats);
      refresh();
    } catch (e) {
      setErr(otErr(t, e));
    }
  }
  async function unsub(s: ScopeSubscription) {
    try {
      await meTreeApi.unsubscribe(p, target(s));
      refresh();
    } catch (e) {
      setErr(otErr(t, e));
    }
  }
  const rows = [...(subs.data?.nodes ?? []), ...(subs.data?.groups ?? [])];
  const groupName = (id?: string) => (groups.data ?? []).find((g) => g.group_id === id)?.name ?? id ?? '';
  return (
    <section className="card" data-testid="node-subs">
      <h2 className="section-title">{t('ot.subs_title')}</h2>
      <p className="muted small">{t('ot.subs_hint')}</p>
      {err && <p className="error">{err}</p>}
      <h3 className="ot-h3">{t('ot.subs_mine')}</h3>
      {subs.isLoading && <p className="muted small">{t('common.loading')}</p>}
      {subs.data && rows.length === 0 && <p className="muted small">{t('ot.subs_none')}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="grid ot-cards">
            <tbody>
              {rows.map((s) => {
                const label = s.group_id ? t('ot.group_prefix', { name: s.name ?? groupName(s.group_id) }) : (s.path ?? [s.name ?? s.node_id]).join(' › ');
                return (
                  <tr key={(s.group_id ?? s.node_id) + (s.delegation_id ?? '')} data-sub={label}>
                    <td data-label={t('ot.subs_target')}>
                      {label}
                      {!s.active && <span className="badge">{t('ot.subs_inactive')}</span>}
                    </td>
                    <td data-label={t('ot.subs_detection')}>
                      <span className="muted small">{t('ot.subs_detection')}</span>{' '}
                      {(['push', 'inbox', 'email'] as Ch[]).map((ch) => (
                        <label key={ch} className="row-inline small">
                          <input type="checkbox" checked={on(s, ch)} onChange={(e) => void setCh(s, ch, e.target.checked)} />
                          {t('ot.ch_' + ch)}
                        </label>
                      ))}
                    </td>
                    <td>
                      <button className="btn btn-sm" onClick={() => void unsub(s)}>
                        {t('ot.unsubscribe')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>
        {t('ot.subs_add')}
      </button>
      <h3 className="ot-h3">{t('ot.implicit_title')}</h3>
      <ul className="plain-list small" data-testid="implicit">
        {subs.data?.implicit && subs.data.implicit.length > 0 ? (
          subs.data.implicit.map((x, i) => (
            <li key={i}>
              <strong>{t('ot.implicit_' + x.kind)}</strong> {x.where} · {t('ot.role_' + x.role, { defaultValue: x.role })} → {x.kind === 'alarm' ? (x.push ? t('ot.implicit_alarm_push') : t('ot.implicit_alarm_feed')) : t('ot.implicit_status')}
            </li>
          ))
        ) : (
          <>
            <li>
              <strong>{t('ot.implicit_alarm')}</strong> {t('ot.implicit_alarm_generic')}
            </li>
            <li>
              <strong>{t('ot.implicit_device_status')}</strong> {t('ot.implicit_status')}
            </li>
          </>
        )}
        <li className="muted">{t('ot.implicit_mfa')}</li>
      </ul>
      {adding && <SubscribePicker close={() => setAdding(false)} done={refresh} groups={(groups.data ?? []).map((g) => ({ id: g.group_id, name: g.name }))} />}
    </section>
  );
}

function SubscribePicker({ close, done, groups }: { close(): void; done(): void; groups: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  const { planes } = useScope();
  const top = useQuery({ queryKey: ['me-tree', 'level', planes[0]?.api, ''], queryFn: () => meTreeApi.level(planes[0]), enabled: planes.length > 0 });
  const [sel, setSel] = useState<{ node_id?: string; delegation_id?: string; group_id?: string; name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    if (!sel) return;
    try {
      await meTreeApi.subscribe(planes[0], sel.group_id ? { group_id: sel.group_id } : { node_id: sel.node_id!, delegation_id: sel.delegation_id });
      done();
      close();
    } catch (e) {
      setErr(otErr(t, e));
    }
  }
  return (
    <Dialog open title={t('ot.subs_add')} onClose={close}>
      <div className="form">
        <ul className="ot-tree" role="tree">
          {(top.data ?? []).map((it) => (
            <PickRow key={it.key} it={{ ...it, _api: planes[0].api }} depth={0} sel={sel} setSel={setSel} />
          ))}
        </ul>
        {groups.length > 0 && (
          <ul className="plain-list">
            {groups.map((g) => (
              <li key={g.id}>
                <label className="row-inline">
                  <input type="radio" name="sub-pick" checked={sel?.group_id === g.id} onChange={() => setSel({ group_id: g.id, name: g.name })} />▦ {g.name}
                </label>
              </li>
            ))}
          </ul>
        )}
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={!sel} onClick={() => void go()}>
            {t('ot.subscribe')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function PickRow({
  it,
  depth,
  sel,
  setSel,
}: {
  it: MeTreeItem & { _api: string };
  depth: number;
  sel: { node_id?: string; delegation_id?: string } | null;
  setSel(s: { node_id: string; delegation_id?: string; name: string }): void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const isOpen = open.has(it.key);
  const kids = useQuery({ queryKey: ['me-tree', 'level', it._api, it.key], queryFn: () => meTreeApi.level({ api: it._api }, it.key), enabled: isOpen });
  if (it.kind === 'home' || it.kind === 'device' || it.kind === 'sharer') return null;
  const blocked = it.delegation?.notice === 'delegator_mfa_required' || it.status === 'ended';
  return (
    <li role="treeitem">
      <div className="ot-row" style={{ paddingLeft: depth * 14 }}>
        {!blocked && it.has_children !== false ? (
          <button className="ot-twisty" aria-label={isOpen ? t('ot.collapse') : t('ot.expand')} onClick={() => setOpen((s) => toggle(s, it.key))}>
            {isOpen ? '▾' : '▸'}
          </button>
        ) : (
          <span className="ot-twisty" />
        )}
        {it.kind === 'node' ? (
          <label className="row-inline">
            <input
              type="radio"
              name="sub-pick"
              checked={sel?.node_id === it.id && (sel?.delegation_id ?? '') === (it.delegation?.delegation_id ?? '')}
              onChange={() => setSel({ node_id: it.id, delegation_id: it.delegation?.delegation_id, name: it.name })}
            />
            {it.name}
          </label>
        ) : (
          <span>{it.kind === 'delegation' ? t('ot.dlg_label', { from: it.delegation?.from_org_name ?? it.name }) : it.name}</span>
        )}
        {it.level_label && <span className="ot-chip">{levelText(t, it.level_label)}</span>}
      </div>
      {isOpen && (
        <ul role="group">
          {(kids.data ?? []).map((x) => (
            <PickRow key={x.key} it={{ ...x, _api: it._api, delegation: x.delegation ?? it.delegation ?? null }} depth={depth + 1} sel={sel} setSel={setSel} />
          ))}
        </ul>
      )}
    </li>
  );
}
